import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  isChunkError: boolean;
  isTimeoutError: boolean;
  errorMessage: string;
}

/**
 * Catches two failure types:
 *  1. Lazy chunk failed to load (network/deployment issue)
 *  2. Runtime error inside a page component
 *
 * Without this, a failed lazy import shows the Suspense
 * fallback (spinner) forever — no crash, no message.
 */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, isTimeoutError: false, errorMessage: '' };

  static getDerivedStateFromError(error: unknown): State {
    const err: Error = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error) || String(error));
    const isChunkError =
      err.message.includes('Loading chunk') ||
      err.message.includes('Failed to fetch dynamically imported module') ||
      err.name === 'ChunkLoadError';
    const isTimeoutError = err.name === 'LazyImportTimeout';
    return { hasError: true, isChunkError, isTimeoutError, errorMessage: err.stack || err.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[PageErrorBoundary]', error instanceof Error ? error.message : String(error), info.componentStack);
  }

  private retry = () => {
    this.setState({ hasError: false, isChunkError: false, isTimeoutError: false, errorMessage: '' });
    // For chunk errors, a hard reload fetches the latest build
    if (this.state.isChunkError || this.state.isTimeoutError) window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center space-y-5 max-w-sm">
          <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="space-y-2">
            <h2 className="text-slate-800 font-black text-lg uppercase tracking-tight">
              {this.state.isChunkError ? 'Update available' : this.state.isTimeoutError ? 'Page load timed out' : 'Page failed to load'}
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              {this.state.isChunkError
                ? 'A newer version of the app is available. Click reload to get it.'
                : this.state.isTimeoutError
                ? 'The page module took too long to load. Reload the app to try again.'
                : 'Something went wrong rendering this page. Try again or contact support.'}
            </p>
          </div>
          <button
            onClick={this.retry}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-black rounded-xl hover:bg-brand-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {this.state.isChunkError || this.state.isTimeoutError ? 'Reload app' : 'Try again'}
          </button>
          {import.meta.env.DEV && this.state.errorMessage && (
            <pre className="mt-4 max-h-48 overflow-auto rounded-xl bg-slate-950 p-4 text-left text-[11px] leading-relaxed text-slate-200">
              {this.state.errorMessage}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
